<template>
  <fieldset>
    <div class="verify" v-if="status !== ValidationResultType.Valid">
      <p class="verify__text" v-html="enterCodeText"></p>
      <div class="verify__phone">
        <p>*** **** **</p>
        <p class="verify__phone-obfuscated">{{ lastTwoDigits }}</p>
      </div>
      <div class="tla-form-group">
        <div class="verify__label" v-html="verificationCodeLabel"></div>
        <div class="verify__input-form">
          <input
            v-model="digit1"
            id="digit1"
            data-vv-name="digit1"
            name="digit1"
            :data-vv-scope="validationScope"
            v-on:keyup="$event.target.nextElementSibling.focus()"
            class="tla-form-group__input verify__input-form verify__input"
            :class="getValidationClass()"
            type="number"
            v-validate.initial="'numeric|length:1'"
            placeholder="-" />
          <input
            v-model="digit2"
            id="digit2"
            data-vv-name="digit2"
            name="digit2"
            :data-vv-scope="validationScope"
            v-on:keyup="$event.target.nextElementSibling.focus()"
            class="tla-form-group__input verify__input-form verify__input"
            :class="getValidationClass()"
            type="number"
            v-validate.initial="'numeric|length:1'"
            placeholder="-" />
          <input
            v-model="digit3"
            id="digit3"
            name="digit3"
            data-vv-name="digit3"
            :data-vv-scope="validationScope"
            v-on:keyup="$event.target.nextElementSibling.focus()"
            class="tla-form-group__input verify__input-form verify__input"
            :class="getValidationClass()"
            type="number"
            v-validate.initial="'numeric|length:1'"
            placeholder="-" />
          <input
            v-model="digit4"
            id="digit4"
            name="digit4"
            data-vv-name="digit4"
            :data-vv-scope="validationScope"
            v-on:keyup="verifyCode()"
            class="tla-form-group__input verify__input-form verify__input"
            :class="getValidationClass()"
            type="number"
            v-validate.initial="'numeric|length:1'"
            placeholder="-" />
        </div>
        <span class="tla-form-group__message tla-form-group__message--error verify__error"
          v-if="hasError && errors.first(`${validationScope}.verification`)">
          {{ errors.first(`${validationScope}.verification`) }}</span>
        <a href="#" class="verify__resend" v-bind:class="{ 'verify__resend--error': hasError }" v-html="resendCodeText"
          @click="openModal()"></a>
      </div>
    </div>
    <div class="verify-success" v-if="status === ValidationResultType.Valid">
      <div class="verify-success__success-animation">
        <svg class="verify-success__green-tick" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
          <circle class="verify-success__circle" cx="26" cy="26" r="25" fill="none"></circle>
          <path class="verify-success__tick" fill="none" d="M14 27l7 7 16-16"></path>
        </svg>
      </div>
      <p class="verify-success__text">Phone number validated successfully!</p>
    </div>
    <div class="verify-overlay" v-bind:class="{ 'verify-overlay--open': modalOpen }">
      <div class="verify-overlay__close" @click="closeModal()">
        <img src="@/assets/images/icon/Close-Icon.svg" />
      </div>
      <div class="verify-overlay__content block__container">
        <div class="verify-overlay__header" v-html="overlayHeaderText"></div>
        <p class="verify-overlay__subheader" v-html="overlaySubheaderText"></p>
        <button class="tla-btn tla-btn--info" type="button" @click="resendCode()" v-html="overlayButtonText"></button>
      </div>
    </div>
  </fieldset>
</template>

<script lang="ts">
import VeeValidate, { Validator } from 'vee-validate';
import {
  Component, Prop, Vue, Watch, Inject
} from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import ValidationResultType from '@/enums/ValidationResultType';
import ExtraData from '@/models/ExtraData';
import { lookupService } from '@/services/LookupService';
import VerificationValidationMessages from '../../validation/verify';

const PersonalDetailsModule = namespace('personalDetailsModule');

Vue.use(VeeValidate, VerificationValidationMessages);

@Component
export default class VerificationCodeComponent extends Vue {
  @PersonalDetailsModule.Getter public personalDetails;

  @PersonalDetailsModule.Action public submitIsValidVerificationCode;

  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 'Please enter the verification code sent to' })
  public enterCodeText!: string;

  @Prop({ default: 'Verification Code' })
  public verificationCodeLabel!: string;

  @Prop({ default: 'Resend code' })
  public resendCodeText!: string;

  @Prop({ default: 'Resend code' })
  public overlayHeaderText!: string;

  @Prop({ default: 'Please check your phone number and submit again' })
  public overlaySubheaderText!: string;

  @Prop({ default: 'Ok' })
  public overlayButtonText!: string;

  @Prop({ default: null })
  public validationScope!: string;

  @Prop({ default: false })
  public usedFromPersonalDetails!: boolean;

  private digit1: string = '';

  private digit2: string = '';

  private digit3: string = '';

  private digit4: string = '';

  private modalOpen: boolean = false;

  private initialCodeSent: boolean = false;

  private verificationStep: number | null = null;

  private status: ValidationResultType = ValidationResultType.NotSet;

  private ValidationResultType = ValidationResultType;

  public get hasError(): boolean {
    return this.status !== ValidationResultType.NotSet && this.status !== ValidationResultType.Valid;
  }

  public get phoneNumber(): string {
    return this.personalDetails.telephone;
  }

  public get fullVerificationCode(): string {
    return this.digit1 + this.digit2 + this.digit3 + this.digit4;
  }

  public get currentStep(): number {
    return this.$store.getters['enquiryModule/currentStep'];
  }

  public get lastTwoDigits(): string {
    return this.phoneNumber.toString().slice(-2);
  }

  public async sendCode() {
    await lookupService.sendVerificationCode(this.personalDetails.telephone);
  }

  public getValidationClass() {
    return this.hasError ? 'tla-form-group__input--error' : '';
  }

  public openModal() {
    this.modalOpen = true;
  }

  public closeModal() {
    this.modalOpen = false;
  }

  public resendCode() {
    this.closeModal();
    this.clearForm();
    this.status = ValidationResultType.NotSet;
    this.submitIsValidVerificationCode(false);
    if (!this.usedFromPersonalDetails) {
      this.prevStep();
    }
  }

  @Watch('status')
  public async checkStatus() {
    if (this.status === ValidationResultType.Valid) {
      this.$validator.errors.removeById('verification');
      this.submitSMSVerificationExtraData();
      this.validateComponent();
    } else {
      this.$validator.errors.add({
        field: 'verification',
        msg: 'Incorrect code entered',
        scope: this.validationScope,
        id: 'verification'
      });
    }
  }

  @Watch('currentStep')
  public resendVerifyCode() {
    if (
      this.initialCodeSent
      && this.verificationStep !== null
      && this.verificationStep === this.currentStep) {
      this.status = ValidationResultType.NotSet;
      this.sendCode();
    }
  }

  public async verifyCode() {
    this.status = ValidationResultType.NotSet;

    const response = await lookupService.verifyTelephoneCode(this.personalDetails.telephone, this.fullVerificationCode);

    if (response === null) {
      return;
    }

    if (response.isValid) {
      this.status = ValidationResultType.Valid;
      this.$emit('code-validated', response.isValid);
      return;
    }
    this.clearForm();
    this.status = ValidationResultType.InvalidCode;
  }

  public async validateComponent() {
    await this.$validator.validateAll(this.validationScope);
  }

  public resetStatus() {
    this.status = ValidationResultType.NotSet;
  }

  public prevStep() {
    this.$store.dispatch('enquiryModule/submitNextStep', this.currentStep - 1);
  }

  public clearForm(): void {
    this.digit1 = '';
    this.digit2 = '';
    this.digit3 = '';
    this.digit4 = '';
  }

  public submitSMSVerificationExtraData() {
    const extraData = new ExtraData('SMSVerification', 'true');
    this.$store.dispatch('enquiryModule/submitExtraData', extraData);
  }

  public async created() {
    this.$validator.localize('en', VerificationValidationMessages.english);
    this.checkStatus();

    await this.sendCode();

    this.verificationStep = this.currentStep;
    this.initialCodeSent = true;
  }
}
</script>
